import { useCallback, useEffect, useId, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";

import { Banner, Field } from "@ui/react";
import { CloseIcon, PinIcon } from "@ui/react/icons";
import { PlacesError, PlacesService, type PlacePrediction } from "../lib/places";
import type { ServiceAreaGeo } from "../lib/types/models";

/**
 * Service-area multi-select backed by Google Places autocomplete.
 *
 * Behaviours that are contractual rather than cosmetic:
 *
 *  - Nothing is ever stored from a prediction. A prediction has no
 *    coordinates, so the details lookup is the only source of a lat/lng and a
 *    failed lookup adds nothing at all — writing the prediction would persist
 *    (0, 0) and break every distance query the contractor appears in.
 *  - 300ms debounce, three-character minimum, and an `ignore` flag so a slow
 *    response for an older query cannot land on top of a newer one.
 *  - With no Places key configured the picker REFUSES to collect areas rather
 *    than falling back to free text. That is not caution: ServiceAreaGeoRequest
 *    binds latitude and longitude as `required`, and Go's `required` rejects the
 *    zero value — so a hand-typed area with no coordinates makes POST
 *    /auth/complete answer 400 with a message that names neither the field nor
 *    the reason, at the last step of onboarding. Better to say plainly that the
 *    feature is not configured than to let a contractor fill in a form that
 *    cannot succeed.
 */
export function ServiceAreaPicker({
  value,
  onChange,
  error,
}: {
  value: ServiceAreaGeo[];
  onChange: (next: ServiceAreaGeo[]) => void;
  error?: string | null;
}) {
  // One service instance per picker, so the Places session token survives
  // re-renders — a new instance per keystroke would defeat session billing.
  const serviceRef = useRef<PlacesService | null>(null);
  if (!serviceRef.current) serviceRef.current = new PlacesService();
  const service = serviceRef.current;
  const searchEnabled = service.enabled;

  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  /** Already-chosen areas are dropped from the list instead of erroring on tap. */
  const visible = useMemo(
    () => predictions.filter((prediction) => !value.some((area) => area.placeId === prediction.placeId)),
    [predictions, value],
  );
  const showList = open && visible.length > 0;

  useEffect(() => {
    if (!searchEnabled) return;

    const input = query.trim();
    if (input.length < 3) {
      setPredictions([]);
      setOpen(false);
      setSearching(false);
      setSearchError(null);
      setActiveIndex(-1);
      return;
    }

    let ignore = false;
    setSearching(true);
    setSearchError(null);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const results = await service.autocomplete(input);
          if (ignore) return;
          setPredictions(results);
          setSearching(false);
          setActiveIndex(-1);
          setOpen(results.length > 0);
        } catch (thrown) {
          if (ignore) return;
          setPredictions([]);
          setSearching(false);
          setOpen(false);
          setSearchError(
            thrown instanceof PlacesError
              ? thrown.message
              : "Address search failed. Please try again.",
          );
        }
      })();
    }, 300);

    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [query, searchEnabled, service]);

  const select = useCallback(
    async (prediction: PlacePrediction) => {
      setOpen(false);
      setActiveIndex(-1);
      setPredictions([]);
      setQuery("");
      setDetailsError(null);

      if (value.some((area) => area.placeId === prediction.placeId)) return;

      setResolving(true);
      const details = await service.details(prediction.placeId);
      setResolving(false);

      if (!details) {
        setDetailsError("We couldn't pin that area down. Please search for it again.");
        return;
      }

      onChange([
        ...value,
        {
          name: details.formattedAddress || details.name,
          placeId: details.placeId,
          latitude: details.latitude,
          longitude: details.longitude,
        },
      ]);
    },
    [onChange, service, value],
  );

  const remove = (index: number) => {
    onChange(value.filter((_, position) => position !== index));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        if (!visible.length) return;
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => (current + 1) % visible.length);
        return;
      case "ArrowUp":
        if (!visible.length) return;
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => (current <= 0 ? visible.length - 1 : current - 1));
        return;
      case "Home":
        if (!showList) return;
        event.preventDefault();
        setActiveIndex(0);
        return;
      case "End":
        if (!showList) return;
        event.preventDefault();
        setActiveIndex(visible.length - 1);
        return;
      case "Enter": {
        if (!showList) return;
        event.preventDefault();
        const chosen = visible[activeIndex];
        if (chosen) void select(chosen);
        return;
      }
      case "Escape":
        if (!open) return;
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        return;
      default:
    }
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && containerRef.current?.contains(next)) return;
    setOpen(false);
    setActiveIndex(-1);
  };

  const fieldError = detailsError ?? searchError ?? error ?? null;
  const activeOptionId = showList && activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined;

  const announcement = resolving
    ? "Loading area details"
    : searching
      ? "Searching for areas"
      : showList
        ? `${visible.length} area suggestion${visible.length === 1 ? "" : "s"} available. Use the up and down arrow keys to review them.`
        : "";

  return (
    <div className="stack stack--tight" ref={containerRef} onBlur={onBlur}>
      {!searchEnabled ? (
        <Banner tone="danger" title="Address search isn't set up yet">
          Shoku matches you to nearby jobs using the exact location of each area you cover, so
          areas can't be added by hand. Set your service areas in the Shoku Contractor app for
          now — everything else here works as normal.
        </Banner>
      ) : null}

      <Field
        label="Service areas"
        hint={
          searchEnabled
            ? "Start typing a suburb or city — at least three letters — then pick it from the list."
            : "Unavailable until address search is configured."
        }
        error={fieldError}
        required
      >
        {({ id, describedBy, invalid }) => (
          <div className="combobox">
            <div className="cluster cluster--nowrap cluster--tight">
              <input
                ref={inputRef}
                id={id}
                className="input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="e.g. Sea Point, Cape Town"
                disabled={!searchEnabled}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setDetailsError(null);
                }}
                onKeyDown={onKeyDown}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                {...(searchEnabled
                  ? {
                      role: "combobox",
                      "aria-expanded": showList,
                      "aria-controls": listId,
                      "aria-autocomplete": "list" as const,
                      "aria-activedescendant": activeOptionId,
                    }
                  : {})}
              />
            </div>

            <ul
              id={listId}
              className="menu"
              role="listbox"
              aria-label="Service area suggestions"
              hidden={!showList}
            >
              {visible.map((prediction, index) => (
                <li
                  key={prediction.placeId}
                  id={`${listId}-option-${index}`}
                  className="menu__item"
                  role="option"
                  aria-selected={index === activeIndex}
                  // mousedown fires before blur, so preventing its default keeps
                  // focus in the input and stops the list closing under the click.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void select(prediction)}
                >
                  <span>{prediction.name}</span>
                  {prediction.formattedAddress && prediction.formattedAddress !== prediction.name ? (
                    <span className="menu__detail">{prediction.formattedAddress}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Field>

      <span className="sr-only" role="status">
        {announcement}
      </span>

      {value.length ? (
        <ul
          className="chip-row"
          aria-label="Selected service areas"
        >
          {value.map((area, index) => (
            <li key={area.placeId ?? `${area.name}-${index}`} className="chip">
              <PinIcon className="icon-sm" />
              <span>{area.name}</span>
              <button
                type="button"
                className="icon-btn icon-btn--sm chip__remove"
                aria-label={`Remove ${area.name}`}
                onClick={() => remove(index)}
              >
                <CloseIcon />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="dim text-sm">
          No service areas added yet.
        </p>
      )}
    </div>
  );
}
